import { useState, type FormEvent } from "react";

import { entrar } from "./api";
import { mensajeDeError } from "./mensajes";

/**
 * Entrar. Es la única pantalla que ve quien no tiene sesión, y la puerta de
 * todo el sistema: la raíz del dominio redirige aquí.
 *
 * El mensaje de error NO distingue «no existe» de «contraseña equivocada», y
 * no es por pereza: la API responde lo mismo a los dos casos a propósito
 * (anti-enumeración, con un hash señuelo para que ni el tiempo los separe),
 * así que inventar aquí una distinción que el servidor se cuidó de no hacer
 * sería deshacer esa decisión desde el cliente. Ver `mensajes.ts`.
 *
 * WCAG 3.3.8: no hay prueba cognitiva, se puede pegar la contraseña y los
 * `autocomplete` dejan trabajar a un gestor de contraseñas.
 */
export function Acceso({ alEntrar }: { alEntrar: () => void | Promise<void> }) {
  const [usuario, setUsuario] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setEnviando(true);
    try {
      await entrar(usuario.trim(), password);
      await alEntrar();
    } catch (err) {
      setError(mensajeDeError(err));
      // La contraseña se borra; el usuario se queda. Volver a teclear el
      // nombre tras un dedazo es castigo sin motivo.
      setPassword("");
    } finally {
      setEnviando(false);
    }
  }

  return (
    <main id="principal" className="centrado">
      <form className="tarjeta acceso" onSubmit={enviar}>
        <h1>Brújula</h1>
        <p className="apagado">Coordinación y administración de la campaña</p>

        <div className="campo">
          <label htmlFor="usuario">Usuario</label>
          <input
            id="usuario"
            name="usuario"
            autoComplete="username"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            required
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
          />
        </div>

        <div className="campo">
          <label htmlFor="password">Contraseña</label>
          <input
            id="password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </div>

        {/* `role="alert"` para que un lector de pantalla lo anuncie: quien no
            ve la pantalla también tiene que enterarse de que falló. */}
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}

        <button
          type="submit"
          className="btn primario"
          disabled={enviando || !usuario || !password}
        >
          {enviando ? "Entrando…" : "Entrar"}
        </button>
      </form>
    </main>
  );
}
